import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';

import { demoPermissions, demoTeams } from '@/data/demo-teams';
import type { Permission } from '@/types/teams';
import { getApiUrl } from '@/lib/api-config';
import { getBackendUrl } from '@/hooks/use-get-backend-url';

const getApiBaseUrl = (): string => {
  return getApiUrl() ?? getBackendUrl();
};

export interface UserPermissions {
  userId: string;
  teamIds: string[];
  grantedPermissions: string[];
  grantedSubPermissions: string[];
}

export interface PermissionsContextValue {
  permissions: Permission[];
  userPermissions: UserPermissions | null;
  loading: boolean;
  error: Error | null;
  hasPermission: (screenKey: string) => boolean;
  hasSubPermission: (subPermissionKey: string) => boolean;
  hasAnyPermissionInCategory: (category: string) => boolean;
  canAccessRoute: (route: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextValue>({
  permissions: [],
  userPermissions: null,
  loading: true,
  error: null,
  hasPermission: () => true,
  hasSubPermission: () => true,
  hasAnyPermissionInCategory: () => true,
  canAccessRoute: () => true,
  refreshPermissions: async () => {},
});

export function usePermissions(): PermissionsContextValue {
  return useContext(PermissionsContext);
}

// Map routes to permission screen keys
const routeToPermissionMap: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/assets': 'assets',
  '/transactions/additions': 'transactions_additions',
  '/transactions/depreciation': 'transactions_depreciation',
  '/transactions/revaluation': 'transactions_revaluation',
  '/tasks': 'tasks',
  '/checkinout': 'checkinout',
  '/compliance': 'compliance',
  '/audits': 'audits_manager',
  '/audits/dashboard': 'audits_manager',
  '/audits/active': 'audits_manager',
  '/audits/scheduled': 'audits_manager',
  '/audits/history': 'audits_manager',
  '/audits/exceptions': 'audits_manager',
  '/insights': 'insights',
  '/enquiry': 'enquiry',
  '/audit': 'audit_trail',
  '/administration/imports': 'import_assets',
  '/administration/import-transactions': 'import_transactions',
  '/administration/locations': 'locations',
  '/administration/categories': 'categories',
  '/administration/equipment': 'equipment',
  '/administration/disposal-reasons': 'disposal_reasons',
  '/administration/teams-permissions': 'team_settings',
  '/settings/features': 'feature_settings',
  '/settings/asset-options': 'asset_options',
  '/help': 'help',
};

interface PermissionsProviderProps {
  readonly children: ReactNode;
  readonly userId?: string;
}

export function PermissionsProvider({ children, userId = 'current_user' }: PermissionsProviderProps): React.ReactElement {
  // 🚨 TEMPORARY: Enable all permissions for everyone until user generation is implemented
  // TODO: Remove this flag once user authentication and team assignment is properly set up
  const TEMP_ENABLE_ALL_PERMISSIONS = true;

  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Check if running in local development mode
  // Includes localhost, 127.0.0.1, local hostnames, and e2b.app development environments
  const isLocalDevelopment = window.location.hostname === 'localhost' || 
                             window.location.hostname === '127.0.0.1' ||
                             window.location.hostname.includes('local') ||
                             window.location.hostname.includes('e2b.app');

  // Check if we should use demo data for permissions
  // This is separate from the current screen's demo/live mode
  // It's based on whether the backend is available
  const [usesDemoPermissions] = useState(() => {
    // In local dev, we'll try to use live API if available, but fall back to demo
    // For now, default to demo mode for permissions unless explicitly configured
    const stored = localStorage.getItem('permissions-mode');
    return stored !== 'live'; // Default to demo unless explicitly set to live
  });

  // Load permission definitions and user permissions
  const loadPermissions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Get all permission definitions first
      setPermissions(demoPermissions);
      
      // TEMPORARY: Grant all permissions to everyone
      if (TEMP_ENABLE_ALL_PERMISSIONS) {
        const allPermIds = demoPermissions.map(p => p.id);
        const allSubPermIds = demoPermissions.flatMap(p => (p.subPermissions ?? []).map(sp => sp.id));
        
        console.log('🚨 TEMPORARY MODE: All permissions enabled for everyone (user generation not yet implemented)', {
          hostname: window.location.hostname,
          permissionCount: allPermIds.length,
          subPermissionCount: allSubPermIds.length,
        });
        
        setUserPermissions({
          userId,
          teamIds: ['temp_all_access'],
          grantedPermissions: allPermIds,
          grantedSubPermissions: allSubPermIds.filter((id): id is string => id !== undefined),
        });
        setLoading(false);
        return;
      }
      
      // In local development mode, grant all permissions (super user)
      if (isLocalDevelopment) {
        const allPermIds = demoPermissions.map(p => p.id);
        const allSubPermIds = demoPermissions.flatMap(p => (p.subPermissions ?? []).map(sp => sp.id));
        
        console.log('🔓 LOCAL DEVELOPMENT MODE: Granting super user permissions', {
          hostname: window.location.hostname,
          permissionCount: allPermIds.length,
          subPermissionCount: allSubPermIds.length,
        });
        
        setUserPermissions({
          userId,
          teamIds: ['local_dev_superuser'],
          grantedPermissions: allPermIds,
          grantedSubPermissions: allSubPermIds.filter((id): id is string => id !== undefined),
        });
        setLoading(false);
        return;
      }

      if (usesDemoPermissions) {
        // Demo permissions - use local data
        // Note: This is independent of whether individual screens are in demo/live mode
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // In demo mode, simulate user being in the Administrators team (full access)
        const adminTeam = demoTeams.find(t => t.id === 'team_default_admin');
        if (adminTeam) {
          const grantedPermissions: string[] = [];
          const grantedSubPermissions: string[] = [];
          
          adminTeam.permissions.forEach(perm => {
            if (perm.granted && perm.permissionId) {
              grantedPermissions.push(perm.permissionId);
              perm.subPermissions?.forEach(subPerm => {
                if (subPerm.granted && subPerm.id) {
                  grantedSubPermissions.push(subPerm.id);
                }
              });
            }
          });
          
          setUserPermissions({
            userId,
            teamIds: [adminTeam.id],
            grantedPermissions,
            grantedSubPermissions,
          });
        } else {
          // Fallback - grant all permissions in demo mode
          const allPermIds = demoPermissions.map(p => p.id);
          const allSubPermIds = demoPermissions.flatMap(p => (p.subPermissions ?? []).map(sp => sp.id));
          
          setUserPermissions({
            userId,
            teamIds: [],
            grantedPermissions: allPermIds,
            grantedSubPermissions: allSubPermIds.filter((id): id is string => id !== undefined),
          });
        }
      } else {
        // Live mode - fetch from API
        const apiBaseUrl = getApiBaseUrl();
        const [permResponse, userPermResponse] = await Promise.all([
          fetch(`${apiBaseUrl}/api/teams/permissions/definitions`),
          fetch(`${apiBaseUrl}/api/teams/permissions/user/${userId}`),
        ]);

        if (!permResponse.ok) {
          throw new Error('Failed to fetch permission definitions');
        }

        if (!userPermResponse.ok) {
          throw new Error('Failed to fetch user permissions');
        }

        const permData = await permResponse.json();
        const userPermData = await userPermResponse.json();

        setPermissions(permData);
        setUserPermissions(userPermData);
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      
      // Fallback to demo data with full access on error
      setPermissions(demoPermissions);
      const allPermIds = demoPermissions.map(p => p.id);
      const allSubPermIds = demoPermissions.flatMap(p => (p.subPermissions ?? []).map(sp => sp.id));
      
      setUserPermissions({
        userId,
        teamIds: [],
        grantedPermissions: allPermIds,
        grantedSubPermissions: allSubPermIds.filter((id): id is string => id !== undefined),
      });
    } finally {
      setLoading(false);
    }
  }, [usesDemoPermissions, userId, isLocalDevelopment]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Check if user has permission to a screen
  const hasPermission = useCallback((screenKey: string): boolean => {
    if (!userPermissions) return true; // Allow access while loading
    
    // Find the permission definition for this screen
    const permDef = permissions.find(p => p.screenKey === screenKey);
    if (!permDef) return true; // If no permission defined, allow access
    
    // Check if user has this permission granted
    return userPermissions.grantedPermissions.includes(permDef.id);
  }, [permissions, userPermissions]);

  // Check if user has a specific sub-permission
  const hasSubPermission = useCallback((subPermissionKey: string): boolean => {
    if (!userPermissions) return true; // Allow access while loading
    
    // Find the sub-permission definition
    for (const perm of permissions) {
      const subPerm = (perm.subPermissions ?? []).find(sp => sp.key === subPermissionKey);
      if (subPerm?.id) {
        return userPermissions.grantedSubPermissions.includes(subPerm.id);
      }
    }
    
    return true; // If no sub-permission defined, allow access
  }, [permissions, userPermissions]);

  // Check if user has any permission in a category
  const hasAnyPermissionInCategory = useCallback((category: string): boolean => {
    if (!userPermissions) return true; // Allow access while loading
    
    const categoryPerms = permissions.filter(p => p.category === category);
    if (categoryPerms.length === 0) return true; // If no permissions in category, allow access
    
    return categoryPerms.some(p => userPermissions.grantedPermissions.includes(p.id));
  }, [permissions, userPermissions]);

  // Check if user can access a specific route
  const canAccessRoute = useCallback((route: string): boolean => {
    if (!userPermissions) return true; // Allow access while loading
    
    // Find the permission screen key for this route
    const screenKey = routeToPermissionMap[route];
    if (!screenKey) return true; // If no permission mapping, allow access
    
    return hasPermission(screenKey);
  }, [hasPermission, userPermissions]);

  const refreshPermissions = useCallback(async () => {
    await loadPermissions();
  }, [loadPermissions]);

  const contextValue = useMemo(() => ({
    permissions,
    userPermissions,
    loading,
    error,
    hasPermission,
    hasSubPermission,
    hasAnyPermissionInCategory,
    canAccessRoute,
    refreshPermissions,
  }), [
    permissions,
    userPermissions,
    loading,
    error,
    hasPermission,
    hasSubPermission,
    hasAnyPermissionInCategory,
    canAccessRoute,
    refreshPermissions,
  ]);

  return (
    <PermissionsContext.Provider value={contextValue}>
      {children}
    </PermissionsContext.Provider>
  );
}
